/**
 * AwardCascadePreview — visual mockup of the proposed contractor lifecycle.
 *
 * Public route: /award-cascade-preview (no auth, like /share-with-bank-preview)
 *
 * Tells the story in 4 panels:
 *   1. The Award cascade — what fires after you click "Award" on a bid
 *   2. The Contractor Portal post-award (their phase + adjacent + project read-only)
 *   3. The smart schedule-aware cadence (when phase N approaches, contact N+1; cascade shifts ripple)
 *   4. The collaboration surface (messages, change orders, daily reports)
 *
 * Zero backend wiring. Pure JSX with mock data so the user can review before
 * any code commits.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2, Mail, Calendar, Phone, FileText, Users, MessageSquare,
  ArrowRight, ArrowDown, Clock, AlertTriangle, ChevronRight, Building2,
  Send, Paperclip, Camera, Bell, Workflow, Network, Sparkles, Eye,
  RefreshCw, GitBranch, Zap, X,
} from 'lucide-react';

// ---------- shared design tokens ----------
const BLUE = '#2563EB';
const BLUE_LIGHT = '#3B82F6';
const BLUE_DARK = '#1D4ED8';

// ---------- mock data ----------
const PROJECT = {
  name: '1615 38th St SE',
  budget: 410000,
  current_phase: 'Framing',
  phases: [
    { num: 1, name: 'Site Prep',     contractor: 'Steve\'s Erosion Ctrl',  status: 'complete', start: 'Jan 8',  end: 'Jan 22'  },
    { num: 2, name: 'Foundation',    contractor: 'JPM Excavation',         status: 'complete', start: 'Jan 23', end: 'Feb 5'   },
    { num: 3, name: 'Framing',       contractor: 'CHL Construction',       status: 'active',   start: 'Feb 6',  end: 'Feb 28', progress: 65 },
    { num: 4, name: 'Mechanicals',   contractor: 'Mid-City Mechanical',    status: 'next',     start: 'Mar 1',  end: 'Mar 22'  },
    { num: 5, name: 'Exterior',      contractor: 'Brain Engineering',      status: 'upcoming', start: 'Mar 18', end: 'Apr 8'   },
    { num: 6, name: 'Interior',      contractor: 'Block 7',                status: 'upcoming', start: 'Apr 5',  end: 'May 1'   },
    { num: 7, name: 'Finish',        contractor: '(unawarded)',            status: 'upcoming', start: 'Apr 28', end: 'May 18' },
    { num: 8, name: 'Indirect',      contractor: '(unawarded)',            status: 'upcoming', start: 'May 8',  end: 'May 22' },
  ],
};

const AWARDED_CONTRACTOR = {
  name: 'CHL Construction',
  contact: 'Jordan Hall',
  email: 'jordan@chlconstruction.com',
  trade: 'Framing',
  phase: 3,
  amount: 67_500,
};

// ---------- tiny helpers ----------
const Section = ({ id, num, title, subtitle, children }) => (
  <section id={id} className="max-w-6xl mx-auto px-6 py-12 border-t border-slate-200" data-testid={`section-${id}`}>
    <div className="flex items-start gap-3 mb-6">
      <span className="flex-shrink-0 w-9 h-9 rounded-full bg-blue-600 text-white font-bold text-sm flex items-center justify-center">{num}</span>
      <div>
        <h2 className="text-2xl font-bold text-slate-900">{title}</h2>
        {subtitle && <p className="text-sm text-slate-600 mt-1 max-w-3xl">{subtitle}</p>}
      </div>
    </div>
    {children}
  </section>
);

const Pill = ({ tone, children }) => {
  const styles = {
    blue:    'bg-blue-100 text-blue-700 border-blue-200',
    emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    amber:   'bg-amber-100 text-amber-700 border-amber-200',
    slate:   'bg-slate-100 text-slate-700 border-slate-200',
    red:     'bg-red-100 text-red-700 border-red-200',
  }[tone];
  return <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${styles}`}>{children}</span>;
};

// ---------- Header ----------
const Header = ({ onClose }) => (
  <header className="sticky top-0 z-30 bg-white border-b border-slate-200 shadow-sm">
    <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${BLUE_LIGHT}, ${BLUE_DARK})` }}>
          <span className="text-white font-bold">D</span>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">DrawReady · Visual Mockup</p>
          <h1 className="text-lg font-bold text-slate-900">Contractor Lifecycle — Award Cascade & Portal Sync</h1>
        </div>
      </div>
      {onClose && (
        <button onClick={onClose} className="text-slate-500 hover:text-slate-900 text-sm flex items-center gap-1" data-testid="close-preview">
          <X className="w-4 h-4" /> Close preview
        </button>
      )}
    </div>
    {/* Sub-nav */}
    <nav className="max-w-6xl mx-auto px-6 pb-2 flex flex-wrap gap-3 text-xs">
      {[
        ['Step 1 · Award Cascade', '#step-1'],
        ['Step 2 · Contractor Portal', '#step-2'],
        ['Step 3 · Smart Cadence', '#step-3'],
        ['Step 4 · Two-Way Collab', '#step-4'],
      ].map(([label, href]) => (
        <a key={href} href={href} className="text-blue-600 hover:text-blue-800 font-medium">{label}</a>
      ))}
    </nav>
  </header>
);

// ==================================================================
// STEP 1 — AWARD CASCADE
// ==================================================================
const Step1AwardCascade = () => (
  <Section
    id="step-1"
    num={1}
    title="Award Cascade — What fires when you click 'Award'"
    subtitle="Today, awarding a contractor sends one email and updates a flag. Proposed: a 5-step cascade that gets the contractor onboarded, scheduled, and into the portal automatically."
  >
    {/* The trigger */}
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-8 flex items-center justify-between">
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">From your Bid Sheet review screen</p>
        <p className="text-sm text-slate-700">
          <span className="font-semibold">{AWARDED_CONTRACTOR.name}</span> · {AWARDED_CONTRACTOR.trade} ·
          <span className="font-mono ml-1">${AWARDED_CONTRACTOR.amount.toLocaleString()}</span>
        </p>
      </div>
      <button className="px-5 py-2.5 rounded-md text-sm font-semibold text-white bg-emerald-600 inline-flex items-center gap-2 shadow-sm">
        <CheckCircle2 className="w-4 h-4" />
        Award Contract
      </button>
    </div>

    {/* The cascade — 5 cards in a stair-step layout */}
    <div className="relative">
      <div className="absolute left-6 top-6 bottom-6 w-px bg-gradient-to-b from-blue-300 via-blue-300 to-transparent" />
      <div className="space-y-4">
        {[
          {
            icon: Mail,
            tone: 'blue',
            title: 'Step 1 · Award confirmation email',
            sub: 'Sent immediately to ' + AWARDED_CONTRACTOR.email,
            preview: 'Subject: You\'ve been awarded — Framing · 1615 38th St SE',
            detail: 'Includes contract summary ($67,500), scope of work attached as PDF, signed-copy upload link, and a "Schedule kickoff call" CTA.',
          },
          {
            icon: Calendar,
            tone: 'blue',
            title: 'Step 2 · Kickoff scheduling request',
            sub: 'Auto-fires 30 minutes after award email',
            preview: 'Subject: Quick kickoff call for Framing scope',
            detail: 'Calendly-style time picker for a 15-min kickoff call. Contractor picks slot → both calendars update → confirmation goes out.',
          },
          {
            icon: Network,
            tone: 'emerald',
            title: 'Step 3 · Portal access provisioned',
            sub: 'Single-link login emailed to the contractor',
            preview: 'Subject: Your DrawReady portal access',
            detail: 'Magic-link login (no password). Lands them on a portal page scoped to their phase + adjacent phases + project read-only view.',
          },
          {
            icon: Bell,
            tone: 'amber',
            title: 'Step 4 · Project Hub timeline entry',
            sub: 'Visible to you and your team',
            preview: '"Awarded Framing to CHL · Kickoff scheduled · Portal access sent"',
            detail: 'All cascade steps logged with timestamps. Becomes the audit trail for "what did we send and when."',
          },
          {
            icon: Workflow,
            tone: 'slate',
            title: 'Step 5 · Adjacent contractors notified',
            sub: 'Lightweight FYI to upstream / downstream phases',
            preview: 'Subject: New crew lined up after your scope on 1615 38th',
            detail: 'Foundation crew (just finished) → "Framing crew confirmed, your work passes inspection on schedule." Mechanicals crew (next) → "Framing scheduled Feb 6 – Feb 28; we\'ll confirm with you Feb 21."',
          },
        ].map((step) => {
          const Icon = step.icon;
          return (
            <div key={step.title} className="relative pl-16">
              <div className="absolute left-0 top-2 w-12 h-12 rounded-full bg-white border-2 border-blue-300 flex items-center justify-center shadow-sm">
                <Icon className="w-5 h-5 text-blue-600" />
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h4 className="text-sm font-semibold text-slate-900">{step.title}</h4>
                  <Pill tone={step.tone}>auto</Pill>
                </div>
                <p className="text-xs text-slate-500 mb-2">{step.sub}</p>
                <div className="bg-slate-50 border-l-2 border-slate-300 px-3 py-1.5 mb-2 text-[11px] font-mono text-slate-700">
                  {step.preview}
                </div>
                <p className="text-xs text-slate-600">{step.detail}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>

    {/* Example: award confirmation email preview */}
    <div className="mt-8">
      <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Example: Step 1 email preview</p>
      <div className="bg-white border border-slate-300 rounded-lg overflow-hidden max-w-2xl">
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 text-[11px] text-slate-600 space-y-0.5">
          <p><span className="font-semibold">From:</span> Mod Williams &lt;noreply@drawready.com&gt;</p>
          <p><span className="font-semibold">To:</span> Jordan Hall &lt;{AWARDED_CONTRACTOR.email}&gt;</p>
          <p><span className="font-semibold">Subject:</span> You've been awarded — Framing · 1615 38th St SE</p>
        </div>
        <div className="p-5 space-y-3 text-sm text-slate-700">
          <p>Hi Jordan,</p>
          <p>You've been awarded the <strong>Framing</strong> scope on <strong>1615 38th St SE</strong>. Here are the details:</p>
          <div className="bg-slate-50 rounded p-3 text-xs space-y-1">
            <p><span className="text-slate-500">Contract amount:</span> <span className="font-mono font-semibold">$67,500</span></p>
            <p><span className="text-slate-500">Expected start:</span> Feb 6</p>
            <p><span className="text-slate-500">Expected completion:</span> Feb 28 (~3 weeks)</p>
            <p><span className="text-slate-500">Phase:</span> 3 of 8</p>
          </div>
          <div className="flex gap-2 flex-wrap pt-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded">
              <Paperclip className="w-3 h-3" /> Scope of Work.pdf
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-blue-600 text-blue-600 text-xs font-semibold rounded">
              <Calendar className="w-3 h-3" /> Schedule kickoff call →
            </span>
          </div>
          <p className="text-xs text-slate-500 pt-2 border-t border-slate-100">You'll get a separate email with your portal access in a few minutes — that's where you'll submit daily reports, photos, and any change orders.</p>
        </div>
      </div>
    </div>
  </Section>
);

// ==================================================================
// STEP 2 — CONTRACTOR PORTAL POST-AWARD
// ==================================================================
const Step2ContractorPortal = () => (
  <Section
    id="step-2"
    num={2}
    title="Contractor Portal — What CHL sees after award"
    subtitle="Their phase highlighted, adjacent phases visible for context, whole project read-only below. Plus a message thread, daily report uploader, and change-order submit form."
  >
    {/* Portal mockup */}
    <div className="bg-slate-900 rounded-t-lg px-5 py-3 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${BLUE_LIGHT}, ${BLUE_DARK})` }}>
          <span className="text-white font-bold text-xs">D</span>
        </div>
        <p className="text-white text-sm font-semibold">Contractor Portal</p>
        <span className="text-slate-400 text-xs">· CHL Construction · 1615 38th St SE</span>
      </div>
      <Pill tone="emerald">Active · Phase 3</Pill>
    </div>
    <div className="bg-white border border-slate-200 rounded-b-lg p-5 space-y-6">
      {/* Your scope block */}
      <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-blue-700 mb-1">Your Active Scope</p>
            <h3 className="text-xl font-bold text-slate-900">Framing — Phase 3</h3>
            <p className="text-xs text-slate-600">Feb 6 → Feb 28 · 65% complete · $67,500 contract · $43,875 drawn</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">On-track status</p>
            <p className="text-lg font-bold text-emerald-700">✓ On Schedule</p>
          </div>
        </div>
        <div className="w-full bg-white rounded-full h-2 overflow-hidden">
          <div className="h-full bg-blue-600" style={{ width: '65%' }} />
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <button className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded font-medium inline-flex items-center gap-1.5"><Camera className="w-3 h-3" />Submit daily report</button>
          <button className="text-xs px-3 py-1.5 bg-white border border-slate-300 rounded font-medium inline-flex items-center gap-1.5"><FileText className="w-3 h-3" />Submit change order</button>
          <button className="text-xs px-3 py-1.5 bg-white border border-slate-300 rounded font-medium inline-flex items-center gap-1.5"><MessageSquare className="w-3 h-3" />Message owner</button>
        </div>
      </div>

      {/* Adjacent phases */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Adjacent phases — for your context</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold text-slate-700">← Phase 2: Foundation</p>
              <Pill tone="emerald">Done</Pill>
            </div>
            <p className="text-[11px] text-slate-500">JPM Excavation · finished Feb 5 · inspection passed</p>
          </div>
          <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold text-slate-700">Phase 4: Mechanicals →</p>
              <Pill tone="blue">Next</Pill>
            </div>
            <p className="text-[11px] text-slate-500">Mid-City Mechanical · starts Mar 1 · 1 day after you finish</p>
          </div>
        </div>
      </div>

      {/* Whole project (read-only) */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><Eye className="w-3 h-3" />Whole project — read-only</p>
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          {PROJECT.phases.map((p, idx) => {
            const isMine = p.num === 3;
            return (
              <div
                key={p.num}
                className={`flex items-center gap-3 px-3 py-2 ${idx < PROJECT.phases.length - 1 ? 'border-b border-slate-100' : ''} ${isMine ? 'bg-blue-50' : ''}`}
              >
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                  p.status === 'complete' ? 'bg-emerald-100 text-emerald-700' :
                  p.status === 'active'   ? 'bg-blue-600 text-white' :
                  p.status === 'next'     ? 'bg-amber-100 text-amber-700' :
                                            'bg-slate-100 text-slate-500'
                }`}>{p.num}</span>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs ${isMine ? 'font-bold text-slate-900' : 'font-medium text-slate-700'} truncate`}>
                    {p.name} {isMine && <span className="text-blue-600">(you)</span>}
                  </p>
                  <p className="text-[10px] text-slate-500 truncate">{p.contractor} · {p.start}–{p.end}</p>
                </div>
                <Pill tone={
                  p.status === 'complete' ? 'emerald' :
                  p.status === 'active'   ? 'blue' :
                  p.status === 'next'     ? 'amber' : 'slate'
                }>{p.status}</Pill>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </Section>
);

// ==================================================================
// STEP 3 — SMART CADENCE
// ==================================================================
const Step3SmartCadence = () => (
  <Section
    id="step-3"
    num={3}
    title="Smart Cadence — Schedule-aware contractor pings"
    subtitle="Instead of blasting everyone every Wednesday, the system contacts the right contractor at the right time based on each phase's start date. When something shifts, it ripples downstream automatically."
  >
    {/* The trigger logic */}
    <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-lg p-5 mb-6">
      <div className="flex items-start gap-3">
        <Zap className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="text-sm font-semibold text-slate-900 mb-1">The rule (proposed)</h4>
          <p className="text-xs text-slate-700 leading-relaxed">
            <strong className="text-blue-700">10 days before a phase's expected start</strong>, the system pings that phase's contractor:
            <em className="text-slate-600"> "Phase {'<previous>'} is on track to wrap {'<date>'}. You're scheduled to start {'<date+1>'} — still good?"</em>
          </p>
          <p className="text-xs text-slate-700 mt-2 leading-relaxed">
            If they say <strong className="text-emerald-700">yes</strong> → log it, no further action.<br />
            If they say <strong className="text-red-700">no, I need 5 more days</strong> → schedule auto-shifts, system pings every <strong>downstream</strong> contractor with the new dates.
          </p>
        </div>
      </div>
    </div>

    {/* Gantt-style visualization */}
    <div className="bg-white border border-slate-200 rounded-lg p-5 mb-6">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-3">Project timeline — 1615 38th St SE</p>
      <div className="space-y-2">
        {PROJECT.phases.slice(0, 6).map((p, idx) => {
          const left = idx * 12;
          const width = 20 + Math.random() * 5; // visual variety
          const bg =
            p.status === 'complete' ? '#10b981' :
            p.status === 'active'   ? BLUE :
            p.status === 'next'     ? '#f59e0b' :
                                      '#cbd5e1';
          return (
            <div key={p.num} className="flex items-center gap-3">
              <div className="w-40 text-xs text-slate-700 truncate flex-shrink-0">
                <span className="font-semibold">{p.num}. {p.name}</span>
                <span className="text-[10px] text-slate-500 block">{p.contractor}</span>
              </div>
              <div className="flex-1 relative h-5 bg-slate-100 rounded">
                <div
                  className="absolute h-full rounded"
                  style={{ left: `${left}%`, width: `${width}%`, background: bg }}
                />
                {p.status === 'active' && (
                  <div className="absolute top-1/2 -translate-y-1/2 text-[9px] font-bold text-white" style={{ left: `${left + 1}%` }}>
                    {p.progress}%
                  </div>
                )}
              </div>
              <span className="text-[10px] text-slate-500 w-24 text-right">{p.start} – {p.end}</span>
            </div>
          );
        })}
      </div>
      {/* Trigger arrow */}
      <div className="mt-4 pt-4 border-t border-dashed border-slate-200 flex items-center gap-2 text-xs text-blue-700">
        <Zap className="w-4 h-4" />
        <span><strong>Today's trigger:</strong> Framing is 65% complete & projected to finish Feb 28. System will ping <strong>Mid-City Mechanical (Phase 4)</strong> tomorrow morning since their start (Mar 1) is now 10 days away.</span>
      </div>
    </div>

    {/* Example check-in email */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Auto-ping example · "You're up next"</p>
        <div className="bg-white border border-slate-300 rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 text-[10px] text-slate-600">
            <p><strong>To:</strong> Mid-City Mechanical</p>
            <p><strong>Subject:</strong> Heads up — you're up next on 1615 38th</p>
          </div>
          <div className="p-3 text-xs text-slate-700 space-y-2">
            <p>Hi team,</p>
            <p>Quick check-in. Framing on <strong>1615 38th St SE</strong> is tracking on schedule (65% complete, projected wrap <strong>Feb 28</strong>).</p>
            <p>You're scheduled to start <strong>Mar 1</strong>. Still good to go?</p>
            <div className="flex gap-2 pt-1">
              <span className="px-2 py-1 bg-emerald-600 text-white rounded text-[10px] font-semibold">✓ Still good</span>
              <span className="px-2 py-1 bg-white border border-slate-300 rounded text-[10px] font-semibold">Need to push</span>
            </div>
          </div>
        </div>
      </div>
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">If they say "push 5 days" · the cascade</p>
        <div className="bg-white border border-slate-300 rounded-lg p-3 space-y-2 text-xs">
          <div className="flex items-center gap-2"><GitBranch className="w-3.5 h-3.5 text-amber-600" /><span><strong>Mechanicals</strong> shifts Mar 1 → <span className="font-mono">Mar 6</span></span></div>
          <ArrowDown className="w-3 h-3 text-slate-400 ml-1.5" />
          <div className="flex items-center gap-2"><GitBranch className="w-3.5 h-3.5 text-amber-600" /><span><strong>Exterior</strong> shifts Mar 18 → <span className="font-mono">Mar 23</span> (auto-emailed)</span></div>
          <ArrowDown className="w-3 h-3 text-slate-400 ml-1.5" />
          <div className="flex items-center gap-2"><GitBranch className="w-3.5 h-3.5 text-amber-600" /><span><strong>Interior</strong> shifts Apr 5 → <span className="font-mono">Apr 10</span> (auto-emailed)</span></div>
          <ArrowDown className="w-3 h-3 text-slate-400 ml-1.5" />
          <div className="flex items-center gap-2"><GitBranch className="w-3.5 h-3.5 text-amber-600" /><span><strong>Finish</strong> shifts Apr 28 → <span className="font-mono">May 3</span> (auto-emailed)</span></div>
          <div className="pt-2 border-t border-slate-100 text-[10px] text-slate-500">Every downstream contractor gets the new dates in one ripple. No spreadsheet, no phone tag.</div>
        </div>
      </div>
    </div>

    {/* Friday recap */}
    <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <Sparkles className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="text-sm font-semibold text-slate-900 mb-1">Friday recap (weekly digest)</h4>
          <p className="text-xs text-slate-700">Every Friday at 4pm, every contractor on every active project gets a one-page recap: <strong>"Here's where we are. Here's what's next. Here's any date changes."</strong> Single PDF attachment so they can forward it to their crew.</p>
        </div>
      </div>
    </div>
  </Section>
);

// ==================================================================
// STEP 4 — TWO-WAY COLLAB
// ==================================================================
const Step4Collab = () => (
  <Section
    id="step-4"
    num={4}
    title="Two-Way Collaboration — Inside the portal"
    subtitle="Once awarded, every conversation lives in the portal. You and the contractor see the same thread, photos, change orders, daily reports — no scattered text messages, no lost emails."
  >
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      {/* Message thread mockup */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-900 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-blue-600" />Messages — Framing scope</p>
            <p className="text-[10px] text-slate-500">CHL Construction · 1615 38th St SE · Phase 3</p>
          </div>
          <Pill tone="blue">Live thread</Pill>
        </div>
        <div className="p-4 space-y-3 text-xs h-72 overflow-y-auto bg-slate-50">
          <div className="flex justify-end">
            <div className="bg-blue-600 text-white rounded-lg rounded-tr-sm px-3 py-2 max-w-xs">
              <p>Hey Jordan, sheathing inspection passed. Good to keep moving on the roof trusses.</p>
              <p className="text-[9px] opacity-70 mt-1">Mod · Feb 14, 9:32 AM</p>
            </div>
          </div>
          <div className="flex justify-start">
            <div className="bg-white border border-slate-200 rounded-lg rounded-tl-sm px-3 py-2 max-w-xs">
              <p>Confirmed. Trusses delivered tomorrow AM. Crew lined up.</p>
              <p className="text-[9px] text-slate-500 mt-1">Jordan · Feb 14, 9:41 AM</p>
            </div>
          </div>
          <div className="flex justify-start">
            <div className="bg-white border border-slate-200 rounded-lg rounded-tl-sm px-3 py-2 max-w-xs">
              <div className="flex items-center gap-1 mb-1 text-[10px] text-slate-500"><Camera className="w-3 h-3" />2 photos</div>
              <div className="grid grid-cols-2 gap-1">
                <div className="aspect-square bg-slate-200 rounded" />
                <div className="aspect-square bg-slate-200 rounded" />
              </div>
              <p className="text-[9px] text-slate-500 mt-1">Jordan · Feb 16, 2:18 PM · Daily report</p>
            </div>
          </div>
          <div className="flex justify-end">
            <div className="bg-blue-600 text-white rounded-lg rounded-tr-sm px-3 py-2 max-w-xs">
              <p>Looks great. FYI sent Mid-City a heads-up they're starting Mar 1.</p>
              <p className="text-[9px] opacity-70 mt-1">Mod · Feb 16, 2:24 PM</p>
            </div>
          </div>
        </div>
        <div className="px-3 py-2 border-t border-slate-200 flex items-center gap-2">
          <input placeholder="Reply…" className="flex-1 text-xs px-3 py-1.5 bg-slate-50 border border-slate-200 rounded" />
          <button className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded font-semibold inline-flex items-center gap-1"><Send className="w-3 h-3" />Send</button>
        </div>
      </div>

      {/* Change order panel */}
      <div className="space-y-4">
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-900 flex items-center gap-2"><FileText className="w-4 h-4 text-amber-600" />Change Orders</p>
            <Pill tone="amber">1 pending</Pill>
          </div>
          <div className="p-3 space-y-2 text-xs">
            <div className="border border-amber-200 bg-amber-50 rounded p-2.5">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-slate-900">CO #1 · Truss redesign</p>
                <span className="font-mono text-slate-700">+$3,200</span>
              </div>
              <p className="text-[11px] text-slate-600 mt-0.5">Engineer required deeper trusses for span. Submitted by Jordan · Feb 12</p>
              <div className="flex gap-2 mt-2">
                <button className="text-[10px] px-2 py-1 bg-emerald-600 text-white rounded font-semibold">Approve</button>
                <button className="text-[10px] px-2 py-1 bg-white border border-slate-300 rounded font-semibold">Reject</button>
                <button className="text-[10px] px-2 py-1 bg-white border border-slate-300 rounded font-semibold">Counter</button>
              </div>
            </div>
            <div className="border border-slate-200 rounded p-2.5 opacity-70">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-slate-900">CO Template · Submit new</p>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
              </div>
              <p className="text-[11px] text-slate-500 mt-0.5">Contractor-side form: scope change, cost, days added, photos.</p>
            </div>
          </div>
        </div>

        {/* Daily report */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-900 flex items-center gap-2"><Camera className="w-4 h-4 text-emerald-600" />Latest Daily Report</p>
            <Pill tone="emerald">Today</Pill>
          </div>
          <div className="p-3 space-y-2 text-xs">
            <p className="text-slate-700"><strong>Crew:</strong> 4 framers · <strong>Hours:</strong> 9.5h · <strong>Weather:</strong> 38°F, clear</p>
            <p className="text-slate-700"><strong>Work done:</strong> Completed second-floor wall framing west elevation. Started ceiling joists.</p>
            <p className="text-slate-700"><strong>Blockers:</strong> None.</p>
            <div className="grid grid-cols-3 gap-1 mt-2">
              <div className="aspect-video bg-slate-200 rounded" />
              <div className="aspect-video bg-slate-200 rounded" />
              <div className="aspect-video bg-slate-200 rounded" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Section>
);

// ==================================================================
// PAGE
// ==================================================================
export default function AwardCascadePreview() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-50" data-testid="award-cascade-preview-page">
      <Header onClose={() => navigate('/dashboard')} />

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-6 py-10 text-center border-b border-slate-200">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-2">Proposal · Visual Mockup</p>
        <h1 className="text-3xl font-bold text-slate-900 mb-3 max-w-3xl mx-auto leading-tight">From "you got the bid" to "the whole crew stays in sync" — automatically.</h1>
        <p className="text-sm text-slate-600 max-w-2xl mx-auto">
          Award a contractor once. The platform handles every follow-up: confirmation, kickoff scheduling, portal access, schedule-aware check-ins, and ripple updates when anything shifts. No more spreadsheets, no more phone tag.
        </p>
        <div className="flex gap-2 justify-center mt-5">
          <a href="#step-1" className="px-4 py-2 bg-blue-600 text-white text-xs font-semibold rounded">See the cascade →</a>
          <a href="#step-3" className="px-4 py-2 bg-white border border-slate-300 text-slate-700 text-xs font-semibold rounded">See smart cadence</a>
        </div>
      </div>

      <Step1AwardCascade />
      <Step2ContractorPortal />
      <Step3SmartCadence />
      <Step4Collab />

      {/* Footer / decision prompt */}
      <Section id="decision" num="?" title="What to build first?" subtitle="Each piece is independent. Pick the order — I'll build it incrementally so you can deploy and use each step before we move on.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { tag: 'A', title: 'Award cascade redesign', desc: 'Better confirmation email, kickoff scheduler, portal magic-link email, Project Hub timeline entry.', cost: '~2–3 hrs', value: 'Immediate — every new award benefits.' },
            { tag: 'B', title: 'Contractor portal expansion', desc: 'Their phase highlighted, adjacent phases visible, project read-only, daily report uploader.', cost: '~3–4 hrs', value: 'Cuts your inbox noise. Contractors stop texting you.' },
            { tag: 'C', title: 'Smart cadence engine', desc: '10-days-before pings, "still good?" replies, automatic downstream ripple emails.', cost: '~3–4 hrs', value: 'This is the magic — turns scheduling into a self-driving process.' },
            { tag: 'D', title: 'Two-way messages + Change Orders', desc: 'In-portal message thread, change-order submit/approve/counter flow.', cost: '~4–5 hrs', value: 'Audit trail. Everything in one place when the bank asks.' },
          ].map((b) => (
            <div key={b.tag} className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 font-bold text-xs flex items-center justify-center">{b.tag}</span>
                <h4 className="text-sm font-semibold text-slate-900">{b.title}</h4>
              </div>
              <p className="text-xs text-slate-600 mb-2">{b.desc}</p>
              <p className="text-[10px] text-slate-500"><span className="font-semibold">Build:</span> {b.cost} · <span className="font-semibold">Value:</span> {b.value}</p>
            </div>
          ))}
        </div>
      </Section>

      <footer className="text-center text-xs text-slate-500 py-8 border-t border-slate-200">
        Visual mockup · DrawReady · Tell me which letter to start with (A/B/C/D) and I'll build it real.
      </footer>
    </div>
  );
}
